/*****************************************************************************
 *   © Copyright 2021   Quantitative Software Management, Inc.               *
 *              --  All rights reserved.  --                                 *
 *   Unpublished -- rights reserved under the Copyright Laws                 *
 *   of the United States.                                                   *
 *                                                                           *
 *   This computer software is protected by trade secret, copyright law,     *
 *   and international treaties and contains proprietary and confidential    *
 *   trade secrets of Quantitative Software Management, Inc.  Unauthorized   *
 *   use, reproduction, or distribution of this program or any portion of    *
 *   it will result in severe civil or criminal penalties and will be        *
 *   prosecuted to the full extent of the law.                               *
 *                                                                           *
 *   Use, duplication, or disclosure by the Government is subject to         *
 *   restrictions as set forth in                                            *
 *      (i) a license agreement incorporated in the contract,                *
 *      (ii) the definition of "restricted rights" in paragraph (c)(1)(ii)   *
 *           of DFARS 252.227-7013 (1988), and                               *
 *      (iii) the definition of "restricted rights" in paragraph (a) of      *
 *            FAR 52.227-14, Alt III (Jun 1987) or                           *
 *      (iv) FAR 52.227-19 (Jun 1987), as applicable.                        *
 *   For purposes of the FAR, this software shall be deemed to               *
 *   be "unpublished" and licensed with disclosure prohibitions.             *
 *   Contractor/Manufacturer is   Quantitative Software Management, Inc.     *
 *                                                                           *
 *****************************************************************************/

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { AppConstants } from '../app.constants';
import { ChartType, DashboardType, SituationType } from '../app.enums';
import {
  DashboardInfo,
  DashboardPart,
  ProbabilityData,
  SolutionComparisonData,
  TimeSeriesChartData,
  PieSlice,
  BarLine,
  QuadrantData,
  GanttData,
  PortfolioFiveStarData,
  PortfolioFiveStarChartData,
  TrendChartData,
  DashboardFolder,
  ChartSize,
  MetricComparisonItem,
  MetricAssessmentInfo,
  RiskData,
  MilestoneGridItem,
  SizeDetails,
  SkillsTimeSeriesData
} from '../Models/chart-data';
import { ContingencyProfile } from '../Models/Contingency';
import { GenericData } from '../Models/Generic-Data';
import { MetricInfo } from '../Models/MetricInfo';
import { UtilityService } from './Utility.Service';

@Injectable({
  providedIn: 'root'
})
export class ChartService {
  constructor(private http: HttpClient, private utilService: UtilityService) { }

  public selectedDashboardSize: ChartSize;
  //public selectedDashboard: DashboardInfo = null;

  public refreshChartComponent: Subject<any> = new Subject<any>();
  public setChartDims: BehaviorSubject<any> = new BehaviorSubject<any>([0, false]);
  public availablePartIds: BehaviorSubject<Array<string>> = new BehaviorSubject<Array<string>>([]);
  public chartPartZoneUpdate: Subject<DashboardPart> = new Subject<DashboardPart>();
  public dashboardModified: Subject<any> = new Subject<any>();

  public logSubscriptionInfo(): void {
    let counts: number[] = [];
    counts.push(this.refreshChartComponent.observers.length);
    counts.push(this.setChartDims.observers.length);
    counts.push(this.availablePartIds.observers.length);
    counts.push(this.chartPartZoneUpdate.observers.length);
    counts.push(this.dashboardModified.observers.length);
    //console.log("%cChart service: subscription counts: ", "color:blue; background-color: lightgray;", counts);
  }

  public GetTimeSeriesData(metricId: number, dashboardType: DashboardType, contingencySetId: number | null): Observable<TimeSeriesChartData> {
    let dest = 'api/charts/timeseriesdata/' + metricId + '/' + dashboardType + '/' + contingencySetId;
    return this.http.get<TimeSeriesChartData>(dest, this.utilService.AjaxOptions());
  }

  public GetPortfolioTimeSeriesData(metricId: number, activeStages: number[]): Observable<TimeSeriesChartData> {
    let dest = 'api/charts/portfoliotimeseriesdata?metricId=' + metricId + '&stages=' + encodeURI(JSON.stringify(activeStages));
    return this.http.get<TimeSeriesChartData>(dest, this.utilService.AjaxOptions());
  }

  public GetTrendData(metricId: number | null, contingencySetId: number | null, historyId: number | null): Observable<TrendChartData> {
    let dest = 'api/charts/GetTrendData/' + metricId + '/' + contingencySetId + '/' + historyId;
    return this.http.get<TrendChartData>(dest, this.utilService.AjaxOptions());
  }

  public SolutionComparisonData(metricId: number | null = null): Observable<SolutionComparisonData[]> {
    let dest = 'api/charts/solutioncomparisondata/' + metricId;
    return this.http.get<SolutionComparisonData[]>(dest, this.utilService.AjaxOptions());
  }

  public SummaryReportData(type: ChartType, dt: DashboardType, profileId: number | null): Observable<MetricComparisonItem[]> {
    return this.http.get<MetricComparisonItem[]>('api/charts/summaryreportdata/' + type + '/' + dt + '/' + profileId, this.utilService.AjaxOptions());
  }

  public GetProjectMetricAssessmentData(metricId: number | null = null): Observable<MetricAssessmentInfo> {
    let dest = 'api/charts/GetProjectMetricAssessmentData/' + metricId;
    return this.http.get<MetricAssessmentInfo>(dest, this.utilService.AjaxOptions());
  }

  public GetSelectedMetric(situation: SituationType): Observable<MetricInfo[]> {
    return this.http.get<MetricInfo[]>('api/charts/getSelectedMetrics/' + situation, this.utilService.AjaxOptions());
  }

  public GetRiskData(): Observable<RiskData[]>  {
    return this.http.get<RiskData[]>('api/charts/GetRiskData', this.utilService.AjaxOptions());
  }

  public GetMilestoneData(contingencyProfileId: number | null): Observable<MilestoneGridItem[]> {
    let dest: string = 'api/charts/GetMilestoneData' + '/' + contingencyProfileId;
    return this.http.get<MilestoneGridItem[]>(dest, this.utilService.AjaxOptions());
  }

  public GetSizeDetailData(): Observable<SizeDetails[]> {
    return this.http.get<SizeDetails[]>('api/charts/GetSizeDetailData', this.utilService.AjaxOptions());
  }

  public GetSkillsBreakoutData(metricId: number | null = null): Observable<SolutionComparisonData[]>{
    let dest = 'api/charts/GetSkillsBreakoutData/' + metricId;
    return this.http.get<SolutionComparisonData[]>(dest, this.utilService.AjaxOptions());
  }

  public GetSkillsTSData(metricId: number | null = null): Observable<SkillsTimeSeriesData> {
    let dest = 'api/charts/GetSkillsTSData/' + metricId;
    return this.http.get<SkillsTimeSeriesData>(dest, this.utilService.AjaxOptions());
  }

  public GetPublicDashboards(dashboardTypeId: number): Observable<GenericData[]> {
    return this.http.get<GenericData[]>('api/charts/GetPublicDashboards/' + dashboardTypeId, this.utilService.AjaxOptions());
  }

  public GetPrivateDashboards(dashboardTypeId: number): Observable<GenericData[]> {
    return this.http.get<GenericData[]>('api/charts/GetPrivateDashboards/' + dashboardTypeId, this.utilService.AjaxOptions());
  }

  public GetDashboardFolders(shared: boolean, dashboardType: DashboardType = null): Observable<DashboardFolder[]> {
    return this.http.get<DashboardFolder[]>(`api/charts/GetDashboardFolders/${shared}/${dashboardType}`, this.utilService.AjaxOptions());
  }

  public GetDashboardInfo(dashboardId: number): Observable<DashboardInfo> {
    return this.http.get<DashboardInfo>('api/charts/GetDashboardInfo/' + dashboardId, this.utilService.AjaxOptions());
  }

  // the returned object is a dictionary with 2 items: 'dashboardId' and 'isFavoriteDashboard'
  public GetDefaultUserDashboardId(dashboardType: DashboardType): Observable<object> {
    return this.http.get<object>('api/charts/GetUserDefaultDashboardId/' + dashboardType, this.utilService.AjaxOptions());
  }

  public SaveDashboard(dashboardInfo: DashboardInfo, saveParts: boolean = true): Observable<number> {
    return this.http.post<number>('api/charts/SaveDashboard/' + saveParts, dashboardInfo, this.utilService.AjaxOptions());
  }

  public SaveDashboards(dashboardsInfo: DashboardInfo[]): Observable<number> {
    return this.http.post<number>('api/charts/SaveDashboards/', dashboardsInfo, this.utilService.AjaxOptions());
  }

  public SaveDashboardFolder(dashboardFolder: DashboardFolder): Observable<DashboardFolder> {
    return this.http.post<DashboardFolder>('api/charts/SaveDashboardFolder/', dashboardFolder, this.utilService.AjaxOptions());
  }

  public SaveDashboardFolders(dashboardFolders: DashboardFolder[]): Observable<DashboardFolder> {
    return this.http.post<DashboardFolder>('api/charts/SaveDashboardFolders/', dashboardFolders, this.utilService.AjaxOptions());
  }

  public RemoveDashboard(dashboardId: number): Observable<boolean> {
    return this.http.post<boolean>('api/charts/RemoveDashboard/' + dashboardId, null, this.utilService.AjaxOptions());
  }

  public RemoveDashboardFolder(dashboardFolderId: number): Observable<boolean> {
    return this.http.post<boolean>('api/charts/RemoveDashboardFolder/' + dashboardFolderId, null, this.utilService.AjaxOptions());
  }

  public LockDashboardFolder(dashboardFolderId: number, lockFolder: boolean): Observable<boolean> {
    return this.http.get<boolean>('api/charts/LockDashboardFolder/' + dashboardFolderId + '/' + lockFolder, this.utilService.AjaxOptions());
  }

  public SetDefaultUserDashboardId(dashboardType: DashboardType, id: number): void {
    let dest: string = `api/charts/SetUserDefaultDashboardId/${dashboardType}/${id}`;
    this.http.get(dest, this.utilService.AjaxOptions()).subscribe();
  }

  public SetUserFavoriteDashboardId(dashboardType: DashboardType, id: number): Observable<number> {
    return this.http.get<number>(`api/charts/SetUserFavoriteDashboardId/${dashboardType}/${id}`, this.utilService.AjaxOptions());
  }

  public StartNewPowerPointExport() {
    return this.http.post('api/charts/StartNewPowerPointExport', null, this.utilService.AjaxOptions());
  }

  public SaveImageForPPT(img: string, name: string) {
    let imageInfo = { 'img': img, 'name': name }
    return this.http.post<any>('api/charts/SaveImageForPPT', imageInfo, this.utilService.AjaxOptions());
  }

  public CreatePowerPointPresentation() {
    return this.http.get('api/charts/CreatePPT', { responseType: 'blob' });
  }

  public GetDashboardsWithoutParts(shared: boolean): Observable<DashboardInfo[]> {
    return this.http.get<DashboardInfo[]>('api/charts/GetDashboardsWithoutParts/' + shared, this.utilService.AjaxOptions());
  }

  public GetContingencyProfiles(): Observable<ContingencyProfile[]> {
    return this.http.get<ContingencyProfile[]>('api/charts/contingencyprofiles', this.utilService.AjaxOptions());
  }

  public GetProbabilityData(metricId: number): Observable<ProbabilityData> {
    let dest = 'api/charts/getprobabilitydata/' + metricId;
    return this.http.get<ProbabilityData>(dest, this.utilService.AjaxOptions());
  }

  public GetTrends(addMostCommon: boolean = true, addProjectTrend: boolean = true): Observable<GenericData[]> {
    let dest = 'api/charts/gettrends';
    return this.http.get<GenericData[]>(dest, this.utilService.AjaxOptions()).pipe(map(
      x => {
        if (addProjectTrend) {
          x.unshift({ Id: AppConstants.PROJECT_TREND_ID, Name: AppConstants.PROJECT_TREND_NAME }); // unshift = add to the front of the array
        }
        if (addMostCommon) {
          x.unshift({ Id: AppConstants.MOST_COMMON_TREND_ID, Name: AppConstants.MOST_COMMON_TREND_NAME }); // unshift = add to the front of the array
        }
        return x;
      }
    ));
  }

  public GetPieData(metricId: number, activeStages: number[]): Observable<PieSlice[]> {
    let dest = 'api/charts/piedata/?metricId=' + metricId + '&stages=' + encodeURI(JSON.stringify(activeStages));
    return this.http.get<PieSlice[]>(dest, this.utilService.AjaxOptions());
  }

  public GetHistogramData(metricId: number, categoryId: number, activeStages: number[]): Observable<BarLine[]> {
    let dest = 'api/charts/histogramdata/?metricId=' + metricId + '&categoryId=' + categoryId + '&stages=' + encodeURI(JSON.stringify(activeStages));
    return this.http.get<BarLine[]>(dest, this.utilService.AjaxOptions());
  }

  public GetQuadrantData(xMetricId: number, yMetricId: number, bubbleMetricId: number, activeStages: number[]): Observable<QuadrantData[]> {
    let dest = 'api/charts/quaddata/?xMetricId=' + xMetricId + '&yMetricId=' + yMetricId + '&bubbleId=' + bubbleMetricId + '&stages=' + encodeURI(JSON.stringify(activeStages));
    return this.http.get<QuadrantData[]>(dest, this.utilService.AjaxOptions());
  }

  public GetGanttData(activeStages: number[]): Observable<GanttData[]> {
    let dest = 'api/charts/ganttdata/?stages=' + encodeURI(JSON.stringify(activeStages));
    return this.http.get<GanttData[]>(dest, this.utilService.AjaxOptions());
  }

  public GetFiveStarData(trendId: number): Observable<PortfolioFiveStarChartData> {
    let dest = 'api/charts/fivestardata/' + trendId;
    return this.http.get<PortfolioFiveStarChartData>(dest, this.utilService.AjaxOptions()).pipe(map(
      x => {
        x.data.forEach((val: PortfolioFiveStarData, ndx: number) => { return new PortfolioFiveStarData(val); });
        return x;
      }
    ));
  }

  public GetMetricAssessmentData(trendId: number, metricId: number): Observable<PortfolioFiveStarChartData> {
    let dest = 'api/charts/metricassessmentdata/' + trendId + '/' + metricId;
    return this.http.get<PortfolioFiveStarChartData>(dest, this.utilService.AjaxOptions()).pipe(map(
      x => {
        x.data.forEach((val: PortfolioFiveStarData, ndx: number) => { return new PortfolioFiveStarData(val); });
        return x;
      }
    ));
  }

  public GetMetricTrendData(metricId: number | null, trendId: number | null, shadingId: number | null): Observable<TrendChartData> {
    let dest = 'api/charts/metrictrenddata/' + metricId + '/' + trendId + '/' + shadingId;
    return this.http.get<TrendChartData>(dest, this.utilService.AjaxOptions());
  }

  public GetSelectedProjectCount(): Observable<number> {
    let dest = 'api/charts/GetSelectedProjectCount';
    return this.http.get<number>(dest, this.utilService.AjaxOptions());
  }
}
